import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { athletesApi, tournamentsApi, statsApi, cartApi } from '../lib/api';
import { rankingsApi } from '../lib/api';
import { toast } from 'sonner';
import MaraSportsLogo from '../components/MaraSportsLogo';
import NotificationBell from '../components/NotificationBell';
import {
    Trophy, Users, Calendar, Target, Zap, LogOut, User,
    ChevronRight, Shield, BarChart3, Search, Home, Settings,
    Award, TrendingUp, MapPin, Menu, X, Plus, ShoppingCart,
    Trash2, CreditCard, Loader2, CheckCircle, Video, Sparkles, Bell
} from 'lucide-react';

const Dashboard = () => {
    const { user, logout, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [athleteProfile, setAthleteProfile] = useState(null);
    const [tournaments, setTournaments] = useState([]);
    const [myRegistrations, setMyRegistrations] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Cart state
    const [cart, setCart] = useState({ items: [], total: 0, currency: 'KES', item_count: 0 });
    const [cartLoading, setCartLoading] = useState(false);
    const [checkingOut, setCheckingOut] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [myRankings, setMyRankings] = useState([]);
    const [myRatings, setMyRatings] = useState([]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/auth');
            return;
        }
        loadData();
        loadCart();
    }, [isAuthenticated, navigate]);

    const loadData = async () => {
        try {
            const [tournamentsRes, statsRes] = await Promise.all([
                tournamentsApi.getAll({ limit: 5 }),
                statsApi.getOverview()
            ]);
            setTournaments(tournamentsRes.data);
            setStats(statsRes.data);

            // Try to get athlete profile
            try {
                const profileRes = await athletesApi.getMyProfile();
                setAthleteProfile(profileRes.data);
                // Fetch rankings for this specific athlete only
                const athleteId = profileRes.data?.id;
                if (athleteId) {
                    try {
                        const detailRes = await rankingsApi.getAthleteRankings({ athlete_id: athleteId });
                        setMyRankings((detailRes.data?.rankings || []).filter(r => r.athlete_id === athleteId));
                        const rateRes = await rankingsApi.getAthleteRatings({ athlete_id: athleteId });
                        setMyRatings((rateRes.data?.ratings || []).filter(r => r.athlete_id === athleteId));
                    } catch (e) { /* Rankings may not exist */ }
                }
            } catch (e) {
                // Profile might not exist yet
            }

            // Get user's tournament registrations
            try {
                const registrationsRes = await tournamentsApi.getMyRegistrations();
                setMyRegistrations(registrationsRes.data.tournaments || []);
            } catch (e) {
                // Registrations might not exist
            }
        } catch (error) {
            console.error('Failed to load data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCart = async () => {
        setCartLoading(true);
        try {
            const res = await cartApi.getCart();
            setCart(res.data);
        } catch (error) {
            console.error('Failed to load cart:', error);
        } finally {
            setCartLoading(false);
        }
    };

    const handleRemoveFromCart = async (registrationId) => {
        setRemovingId(registrationId);
        try {
            await cartApi.removeItem(registrationId);
            toast.success('Item removed from cart');
            loadCart();
        } catch (error) {
            toast.error('Failed to remove item');
        } finally {
            setRemovingId(null);
        }
    };

    const handleCheckout = async () => {
        setCheckingOut(true);
        try {
            const res = await cartApi.checkout();
            toast.success(`Payment successful! ${res.data.registrations_confirmed} registration(s) confirmed.`);
            loadCart();
            loadData(); // Refresh registrations
            setActiveTab('overview');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Checkout failed');
        } finally {
            setCheckingOut(false);
        }
    };

    const getCurrencySymbol = (currency) => {
        const symbols = { KES: 'KSh ', USD: '$', EUR: '€', GBP: '£' };
        return symbols[currency] || currency + ' ';
    };

    const handleLogout = () => {
        logout();
        toast.success('Logged out successfully');
        navigate('/');
    };

    const navItems = [
        { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard', active: true },
        { icon: <User className="w-5 h-5" />, label: 'My Profile', path: '/profile' },
        { icon: <Trophy className="w-5 h-5" />, label: 'Tournaments', path: '/tournaments' },
        { icon: <Zap className="w-5 h-5" />, label: 'Live Scores & Streaming', path: '/live-scores' },
        { icon: <ShoppingCart className="w-5 h-5" />, label: 'Marketplace', path: '/marketplace' },
        { icon: <Video className="w-5 h-5" />, label: 'AI Video Analysis', path: '/video-analysis' },
        { icon: <Search className="w-5 h-5" />, label: 'Scouting Portal', path: '/scouting' },
        { icon: <Award className="w-5 h-5" />, label: 'School Rankings', path: '/schools/leaderboard' },
        { icon: <Search className="w-5 h-5" />, label: 'Talent Discovery', path: '/talent' },
        { icon: <BarChart3 className="w-5 h-5" />, label: 'Leaderboards', path: '/leaderboards' },
        { icon: <TrendingUp className="w-5 h-5" />, label: 'National Rankings', path: '/rankings' },
        { icon: <Target className="w-5 h-5" />, label: 'AI Predictions', path: '/ai-predictions' },
        { icon: <Sparkles className="w-5 h-5" />, label: 'How It Works', path: '/how-it-works' },
        { icon: <Bell className="w-5 h-5" />, label: 'Notifications', path: '/notifications' },
    ];

    const getStatusColor = (tournament_status) => {
        switch (tournament_status) {
            case 'registration_open': return 'text-success bg-success/10';
            case 'in_progress': return 'text-secondary bg-secondary/10';
            case 'upcoming': return 'text-accent bg-accent/10';
            case 'completed': return 'text-muted-foreground bg-muted';
            default: return 'text-muted-foreground bg-muted';
        }
    };

    const getStatusLabel = (tournament_status) => {
        switch (tournament_status) {
            case 'registration_open': return 'Registration Open';
            case 'in_progress': return 'In Progress';
            case 'upcoming': return 'Upcoming';
            case 'completed': return 'Completed';
            default: return tournament_status;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] text-white flex">
            {/* Grid overlay */}
            <div className="fixed inset-0 grid-lines pointer-events-none z-0" />

            {/* Sidebar */}
            <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0d2847] border-r border-white/5 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static`}>
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="p-6 border-b border-white/5">
                        <Link to="/">
                            <MaraSportsLogo className="h-10" />
                        </Link>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-1">
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                                    item.active 
                                        ? 'bg-white/5 text-white border-l-2 border-primary' 
                                        : 'text-muted-foreground hover:text-white hover:bg-white/5'
                                }`}
                                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            >
                                {item.icon}
                                <span className="text-sm font-medium">{item.label}</span>
                            </Link>
                        ))}
                    </nav>

                    {/* User section */}
                    <div className="p-4 border-t border-white/5">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-primary/20 flex items-center justify-center">
                                <User className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleLogout}
                            className="w-full border-white/10 text-muted-foreground hover:text-white"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main content */}
            <main className="flex-1 relative z-10">
                {/* Top bar */}
                <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                    <div className="flex items-center justify-between h-14 md:h-16 px-4 md:px-6">
                        <div className="flex items-center gap-2 md:gap-4">
                            <button
                                className="lg:hidden p-2 text-muted-foreground hover:text-white"
                                onClick={() => setSidebarOpen(true)}
                                data-testid="mobile-sidebar-btn"
                            >
                                <Menu className="w-5 h-5 md:w-6 md:h-6" />
                            </button>
                            <h1 className="font-heading font-bold text-base md:text-xl uppercase tracking-tight">Dashboard</h1>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4">
                            <button 
                                className={`flex items-center gap-1 md:gap-2 px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm transition-colors ${activeTab === 'cart' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-white'}`}
                                onClick={() => setActiveTab('cart')}
                                data-testid="cart-tab-btn"
                            >
                                <ShoppingCart className="w-4 h-4" />
                                <span className="hidden sm:inline">Cart</span>
                                {cart.item_count > 0 && (
                                    <span className="bg-primary text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                                        {cart.item_count}
                                    </span>
                                )}
                            </button>
                            <NotificationBell />
                        </div>
                    </div>
                    {/* Tab Navigation */}
                    <div className="flex border-t border-white/5">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`flex-1 py-2 md:py-3 text-xs md:text-sm font-medium transition-colors border-b-2 ${
                                activeTab === 'overview' 
                                    ? 'border-primary text-white' 
                                    : 'border-transparent text-muted-foreground hover:text-white'
                            }`}
                            data-testid="overview-tab"
                        >
                            Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('cart')}
                            className={`flex-1 py-2 md:py-3 text-xs md:text-sm font-medium transition-colors border-b-2 flex items-center justify-center gap-1 md:gap-2 ${
                                activeTab === 'cart' 
                                    ? 'border-primary text-white' 
                                    : 'border-transparent text-muted-foreground hover:text-white'
                            }`}
                            data-testid="cart-tab"
                        >
                            <ShoppingCart className="w-3 h-3 md:w-4 md:h-4" />
                            <span className="hidden sm:inline">Cart &</span> Checkout
                            {cart.item_count > 0 && (
                                <span className="bg-primary text-white text-xs px-1.5 md:px-2 py-0.5 rounded-full">
                                    {cart.item_count}
                                </span>
                            )}
                        </button>
                    </div>
                </header>

                {/* Dashboard content */}
                <div className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8">
                    {activeTab === 'overview' && (
                    <>
                    {/* Welcome section */}
                    <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                                <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight mb-2">
                                    Welcome back, {user?.full_name?.split(' ')[0]}!
                                </h2>
                                <p className="text-muted-foreground">
                                    {athleteProfile 
                                        ? 'Your athlete passport is active. Keep building your legacy.'
                                        : 'Complete your athlete profile to start your journey.'
                                    }
                                </p>
                            </div>
                            {!athleteProfile && user?.role === 'athlete' && (
                                <Button
                                    onClick={() => navigate('/profile')}
                                    className="bg-primary hover:bg-red-600 text-white btn-glow uppercase tracking-widest text-xs font-bold"
                                    data-testid="create-profile-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create Profile
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Athlete Rankings Badges */}
                    {myRankings.length > 0 && (
                        <div className="bg-gradient-to-r from-[#0d2847] to-[#1a1a2e] border border-yellow-500/20 p-4 md:p-6" data-testid="my-rankings-section">
                            <div className="flex items-center gap-2 mb-3">
                                <Trophy className="w-5 h-5 text-yellow-400" />
                                <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-white/80">My Rankings</h3>
                                <Link to="/rankings" className="ml-auto text-xs text-primary hover:text-red-400 flex items-center gap-1">
                                    View All Rankings <ChevronRight className="w-3 h-3" />
                                </Link>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {myRankings.map((r, i) => {
                                    const rating = myRatings.find(rt => rt.sport === r.sport);
                                    const tierColors = {
                                        tier_5: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-400',
                                        tier_4: 'border-purple-500/40 bg-purple-500/10 text-purple-400',
                                        tier_3: 'border-blue-500/40 bg-blue-500/10 text-blue-400',
                                        tier_2: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
                                        tier_1: 'border-slate-500/40 bg-slate-500/10 text-slate-400',
                                    };
                                    const tierNames = { tier_5: 'Champion', tier_4: 'High Perf', tier_3: 'Elite', tier_2: 'Performance', tier_1: 'Development' };
                                    return (
                                        <div key={r.sport + r.age_group + i} className="bg-[#0a1628] border border-white/10 rounded-lg p-3 flex items-start gap-3" data-testid={`ranking-badge-${r.sport}`}>
                                            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
                                                <span className="text-yellow-400 font-bold text-lg">#{r.rank_position}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-white font-medium text-sm truncate">{r.sport}</span>
                                                    <span className="text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded">{r.age_group}</span>
                                                </div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-white/60 text-xs">{r.total_points.toLocaleString()} pts</span>
                                                    {rating && (
                                                        <span className="text-purple-400 text-xs flex items-center gap-0.5">
                                                            <TrendingUp className="w-3 h-3" /> ELO {rating.current_rating}
                                                        </span>
                                                    )}
                                                </div>
                                                {r.sponsorship_tier && (
                                                    <span className={`mt-1 inline-block text-[10px] px-2 py-0.5 rounded border ${tierColors[r.sponsorship_tier] || ''}`}>
                                                        {tierNames[r.sponsorship_tier] || r.sponsorship_tier}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-[#0d2847] border border-white/5 p-6" data-testid="stat-athletes">
                            <Users className="w-6 h-6 text-primary mb-3" />
                            <p className="stat-display text-3xl md:text-4xl">{stats?.total_athletes || 0}</p>
                            <p className="text-xs tracking-widest uppercase text-muted-foreground mt-1">Total Athletes</p>
                        </div>
                        <div className="bg-[#0d2847] border border-white/5 p-6" data-testid="stat-tournaments">
                            <Trophy className="w-6 h-6 text-accent mb-3" />
                            <p className="stat-display text-3xl md:text-4xl">{stats?.total_tournaments || 0}</p>
                            <p className="text-xs tracking-widest uppercase text-muted-foreground mt-1">Tournaments</p>
                        </div>
                        <div className="bg-[#0d2847] border border-white/5 p-6" data-testid="stat-verified">
                            <Shield className="w-6 h-6 text-success mb-3" />
                            <p className="stat-display text-3xl md:text-4xl">{stats?.verified_athletes || 0}</p>
                            <p className="text-xs tracking-widest uppercase text-muted-foreground mt-1">Verified</p>
                        </div>
                        <div className="bg-[#0d2847] border border-white/5 p-6" data-testid="stat-active">
                            <Zap className="w-6 h-6 text-secondary mb-3" />
                            <p className="stat-display text-3xl md:text-4xl">{stats?.active_tournaments || 0}</p>
                            <p className="text-xs tracking-widest uppercase text-muted-foreground mt-1">Active Now</p>
                        </div>
                    </div>

                    {/* Featured New Sports */}
                    <div className="bg-gradient-to-r from-[#0d2847] to-[#1a1a2e] border border-white/5 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading font-bold text-lg uppercase tracking-tight flex items-center gap-2">
                                <Zap className="w-5 h-5 text-yellow-400" />
                                New Sports Added
                            </h3>
                            <Link to="/tournaments" className="text-xs text-primary hover:text-red-400 flex items-center gap-1">
                                Browse All <ChevronRight className="w-3 h-3" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* E-Sports */}
                            <Link 
                                to="/tournaments?sport=E-Sports"
                                className="bg-[#0a1628] border border-purple-500/30 p-4 hover:border-purple-500/60 transition-all group"
                                data-testid="featured-esports"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-3xl">🎮</span>
                                    <div>
                                        <h4 className="font-bold text-purple-400 group-hover:text-purple-300">E-SPORTS</h4>
                                        <p className="text-xs text-muted-foreground">Digital Competition</p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {['Valorant', 'FIFA', 'CoD', 'LoL'].map(game => (
                                        <span key={game} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">
                                            {game}
                                        </span>
                                    ))}
                                </div>
                            </Link>

                            {/* Motor Racing */}
                            <Link 
                                to="/tournaments?sport=Motor%20Racing"
                                className="bg-[#0a1628] border border-red-500/30 p-4 hover:border-red-500/60 transition-all group"
                                data-testid="featured-racing"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-3xl">🏎️</span>
                                    <div>
                                        <h4 className="font-bold text-red-400 group-hover:text-red-300">MOTOR RACING</h4>
                                        <p className="text-xs text-muted-foreground">Speed & Precision</p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {['Karting', 'Formula', 'Rally', 'Sim Racing'].map(cat => (
                                        <span key={cat} className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded">
                                            {cat}
                                        </span>
                                    ))}
                                </div>
                            </Link>

                            {/* Squash */}
                            <Link 
                                to="/tournaments?sport=Squash"
                                className="bg-[#0a1628] border border-orange-500/30 p-4 hover:border-orange-500/60 transition-all group"
                                data-testid="featured-squash"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-3xl">🎾</span>
                                    <div>
                                        <h4 className="font-bold text-orange-400 group-hover:text-orange-300">SQUASH</h4>
                                        <p className="text-xs text-muted-foreground">Court Mastery</p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {['Singles', 'Doubles', 'Mixed'].map(format => (
                                        <span key={format} className="text-xs bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded">
                                            {format}
                                        </span>
                                    ))}
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Two column layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Upcoming Tournaments */}
                        <div className="bg-[#0d2847] border border-white/5 p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="font-heading font-bold text-lg uppercase tracking-tight">
                                    Upcoming Tournaments
                                </h3>
                                <Link to="/tournaments" className="text-xs text-primary hover:text-red-400 flex items-center gap-1">
                                    View All <ChevronRight className="w-3 h-3" />
                                </Link>
                            </div>
                            <div className="space-y-4">
                                {tournaments.slice(0, 4).map((tournament) => (
                                    <Link
                                        key={tournament.id}
                                        to={`/tournaments/${tournament.id}`}
                                        className="block p-4 bg-[#0a1628] border border-white/5 hover:border-white/10 transition-colors"
                                        data-testid={`tournament-card-${tournament.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-medium text-sm mb-1 truncate">{tournament.name}</h4>
                                                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <MapPin className="w-3 h-3" />
                                                        {tournament.location}
                                                    </span>
                                                    <span>{tournament.sport}</span>
                                                </div>
                                            </div>
                                            <span className={`text-xs px-2 py-1 ${getStatusColor(tournament.tournament_status)}`}>
                                                {getStatusLabel(tournament.tournament_status)}
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                                {tournaments.length === 0 && (
                                    <p className="text-center text-muted-foreground py-8">No tournaments found</p>
                                )}
                            </div>
                        </div>

                        {/* My Registrations */}
                        {myRegistrations.length > 0 && (
                        <div className="bg-[#0d2847] border border-white/5 p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="font-heading font-bold text-lg uppercase tracking-tight flex items-center gap-2">
                                    <Trophy className="w-5 h-5 text-accent" />
                                    My Registrations
                                </h3>
                                <span className="text-xs bg-accent/20 text-accent px-2 py-1">
                                    {myRegistrations.length} Tournament{myRegistrations.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <div className="space-y-4">
                                {myRegistrations.map((tournament) => (
                                    <Link
                                        key={tournament.id}
                                        to={`/tournaments/${tournament.id}`}
                                        className="block p-4 bg-[#0a1628] border border-accent/20 hover:border-accent/40 transition-colors"
                                        data-testid={`my-registration-${tournament.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-medium text-sm mb-1 truncate">{tournament.name}</h4>
                                                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {tournament.start_date}
                                                    </span>
                                                    <span>{tournament.sport}</span>
                                                </div>
                                            </div>
                                            <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400">
                                                Registered
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                        )}

                        {/* Quick Actions */}
                        <div className="bg-[#0d2847] border border-white/5 p-6">
                            <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-6">
                                Quick Actions
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <Link
                                    to="/tournaments"
                                    className="p-4 bg-[#0a1628] border border-white/5 hover:border-primary/50 transition-colors text-center"
                                    data-testid="quick-action-tournaments"
                                >
                                    <Calendar className="w-8 h-8 text-primary mx-auto mb-3" />
                                    <p className="text-sm font-medium">Browse Tournaments</p>
                                </Link>
                                <Link
                                    to="/talent"
                                    className="p-4 bg-[#0a1628] border border-white/5 hover:border-accent/50 transition-colors text-center"
                                    data-testid="quick-action-talent"
                                >
                                    <Search className="w-8 h-8 text-accent mx-auto mb-3" />
                                    <p className="text-sm font-medium">Discover Talent</p>
                                </Link>
                                <Link
                                    to="/leaderboards"
                                    className="p-4 bg-[#0a1628] border border-white/5 hover:border-success/50 transition-colors text-center"
                                    data-testid="quick-action-leaderboards"
                                >
                                    <BarChart3 className="w-8 h-8 text-success mx-auto mb-3" />
                                    <p className="text-sm font-medium">View Rankings</p>
                                </Link>
                                <Link
                                    to="/ai-predictions"
                                    className="p-4 bg-[#0a1628] border border-white/5 hover:border-secondary/50 transition-colors text-center"
                                    data-testid="quick-action-ai"
                                >
                                    <Zap className="w-8 h-8 text-secondary mx-auto mb-3" />
                                    <p className="text-sm font-medium">AI Predictions</p>
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Sports breakdown */}
                    <div className="bg-[#0d2847] border border-white/5 p-6">
                        <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-6">
                            Athletes by Sport
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {stats?.sports_breakdown?.map((sport) => (
                                <div key={sport.sport} className="text-center p-4 bg-[#0a1628] border border-white/5">
                                    <p className="stat-display text-2xl text-primary">{sport.athletes}</p>
                                    <p className="text-xs text-muted-foreground mt-1">{sport.sport}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    </>
                    )}

                    {/* Cart & Checkout Tab */}
                    {activeTab === 'cart' && (
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center gap-4 mb-8">
                            <ShoppingCart className="w-8 h-8 text-primary" />
                            <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight">
                                Your Cart
                            </h2>
                        </div>

                        {cartLoading ? (
                            <div className="bg-[#0d2847] border border-white/5 p-12 text-center">
                                <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                <p className="text-muted-foreground">Loading cart...</p>
                            </div>
                        ) : cart.items.length === 0 ? (
                            <div className="bg-[#0d2847] border border-white/5 p-12 text-center">
                                <ShoppingCart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                                <h3 className="font-heading text-xl uppercase mb-2">Your cart is empty</h3>
                                <p className="text-muted-foreground mb-6">Browse tournaments and register to add items to your cart</p>
                                <Button
                                    onClick={() => navigate('/tournaments')}
                                    className="bg-primary hover:bg-red-600 text-white uppercase tracking-widest"
                                    data-testid="browse-tournaments-btn"
                                >
                                    Browse Tournaments
                                </Button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Cart Items */}
                                <div className="lg:col-span-2 space-y-4">
                                    {cart.items.map((item) => (
                                        <div
                                            key={item.id}
                                            className="bg-[#0d2847] border border-white/5 p-6"
                                            data-testid={`cart-item-${item.id}`}
                                        >
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex-1">
                                                    <h4 className="font-heading font-bold text-lg uppercase tracking-tight mb-2">
                                                        {item.tournament_name}
                                                    </h4>
                                                    <div className="space-y-1 text-sm text-muted-foreground">
                                                        <p className="flex items-center gap-2">
                                                            <Trophy className="w-4 h-4" />
                                                            {item.sport}
                                                        </p>
                                                        <p>Category: {item.age_category}</p>
                                                        {item.gender_category && (
                                                            <p>Gender: {item.gender_category}</p>
                                                        )}
                                                        {item.disciplines?.length > 0 && (
                                                            <div className="mt-2">
                                                                <p className="text-xs uppercase tracking-wider mb-1">Events:</p>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {item.disciplines.map((d, i) => (
                                                                        <span key={i} className="px-2 py-0.5 bg-primary/10 border border-primary/20 text-xs">
                                                                            {d}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-mono text-xl text-accent">
                                                        {getCurrencySymbol(item.currency)}{item.registration_fee?.toLocaleString()}
                                                    </p>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRemoveFromCart(item.id)}
                                                        disabled={removingId === item.id}
                                                        className="text-muted-foreground hover:text-red-500 mt-2"
                                                        data-testid={`remove-item-${item.id}`}
                                                    >
                                                        {removingId === item.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="w-4 h-4" />
                                                        )}
                                                        <span className="ml-1 text-xs">Remove</span>
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Order Summary */}
                                <div className="lg:col-span-1">
                                    <div className="bg-[#0d2847] border border-white/5 p-6 sticky top-40">
                                        <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-6">
                                            Order Summary
                                        </h3>

                                        <div className="space-y-3 text-sm">
                                            <div className="flex justify-between text-muted-foreground">
                                                <span>Registrations</span>
                                                <span>{cart.item_count}</span>
                                            </div>
                                            <div className="flex justify-between text-muted-foreground">
                                                <span>Subtotal</span>
                                                <span>{getCurrencySymbol(cart.currency)}{cart.total?.toLocaleString()}</span>
                                            </div>
                                            <div className="border-t border-white/10 pt-3 mt-3">
                                                <div className="flex justify-between items-center">
                                                    <span className="font-bold">Total</span>
                                                    <span className="font-mono text-2xl text-accent" data-testid="cart-total">
                                                        {getCurrencySymbol(cart.currency)}{cart.total?.toLocaleString()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <Button
                                            onClick={handleCheckout}
                                            disabled={checkingOut || cart.items.length === 0}
                                            className="w-full bg-primary hover:bg-red-600 text-white uppercase tracking-widest mt-6 h-12"
                                            data-testid="checkout-btn"
                                        >
                                            {checkingOut ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <CreditCard className="w-4 h-4 mr-2" />
                                            )}
                                            {checkingOut ? 'Processing...' : 'Proceed to Payment'}
                                        </Button>

                                        <p className="text-xs text-muted-foreground text-center mt-4">
                                            Payment is simulated for demo purposes
                                        </p>

                                        <div className="mt-6 pt-6 border-t border-white/10">
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <CheckCircle className="w-4 h-4 text-success" />
                                                <span>Secure checkout</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                                                <CheckCircle className="w-4 h-4 text-success" />
                                                <span>Instant confirmation</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
